import type { Prototype } from '@atlas/api-contract';
import { Layers, Plus, SearchX } from 'lucide-react';
import { useCallback, useState } from 'react';

import { PaginationBar } from '@/components/pagination-bar';
import { EmptyState, ErrorState, SkeletonGrid } from '@/components/states';
import { Button } from '@/components/ui/button';
import { useCan } from '@/features/auth';
import { UploadVersionDialog } from '@/features/versions/upload-version-dialog';
import { cn } from '@/lib/utils';

import { CreatePrototypeDialog } from './create-prototype-dialog';
import { DeletePrototypeDialog } from './delete-prototype-dialog';
import { EditPrototypeDialog } from './edit-prototype-dialog';
import { PrototypeCard } from './prototype-card';
import { usePrototypesQuery } from './prototypes-queries';
import {
  prototypesSearchCodec,
  type PrototypeSort,
  type PrototypesSearch,
} from './prototypes-search';
import { PrototypesToolbar } from './prototypes-toolbar';

export interface PrototypesBrowserProps {
  projectId: string;
  /** The URL's search state. The single source of truth for this view. */
  search: PrototypesSearch;
  /**
   * Write new search state back to the URL. `replace` is for changes the back
   * button should not step through one at a time — i.e. typing.
   */
  onSearchChange: (next: PrototypesSearch, replace?: boolean) => void;
}

/**
 * The prototype list inside one project (spec R6.6, R6.7).
 *
 * The same contract as `ProjectsBrowser`: every control writes to the URL and
 * reads its value back from it, so a reload, a bookmark and a pasted link all
 * restore the same view, and all three list states are wired to the real query.
 *
 * The one addition is the upload dialog, held here rather than on the card: it
 * has to survive the card being re-rendered out from under it when the list
 * refetches, and there is only ever one upload in flight.
 */
export const PrototypesBrowser = ({
  projectId,
  search,
  onSearchChange,
}: PrototypesBrowserProps) => {
  const resolved = prototypesSearchCodec.resolve(search);
  const query = usePrototypesQuery(projectId, search);

  const can = useCan();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Prototype | null>(null);
  const [deleting, setDeleting] = useState<Prototype | null>(null);
  const [uploading, setUploading] = useState<Prototype | null>(null);

  /*
   * Creating a prototype and uploading a version are Designer/Admin actions
   * (spec matrix). The cards and toolbar hide the controls a role cannot use;
   * these openers just move state, and the API is the real gate (spec R7.8).
   */
  const openCreate = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const openUpload = useCallback((prototype: Prototype) => {
    setUploading(prototype);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      // A new filter invalidates the current page number, so reset to the first.
      onSearchChange(
        prototypesSearchCodec.parse({ ...search, search: value, page: 1 }),
        true,
      );
    },
    [onSearchChange, search],
  );

  const handleSortChange = useCallback(
    (value: PrototypeSort) => {
      onSearchChange(
        prototypesSearchCodec.parse({ ...search, sort: value, page: 1 }),
      );
    },
    [onSearchChange, search],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      onSearchChange(prototypesSearchCodec.parse({ ...search, page }));
    },
    [onSearchChange, search],
  );

  const goToFirstPage = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  const isFiltered = resolved.search !== '';

  const renderList = () => {
    if (query.isPending) {
      return <SkeletonGrid count={6} label="Loading prototypes" />;
    }

    if (query.isError) {
      return (
        <ErrorState
          title="Could not load prototypes"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    }

    const { items, total } = query.data;

    if (items.length === 0) {
      if (isFiltered) {
        return (
          <EmptyState
            icon={SearchX}
            title={`No prototypes match “${resolved.search}”`}
            description="Search covers prototype names and descriptions. Try a shorter term."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  handleSearchChange('');
                }}
              >
                Clear search
              </Button>
            }
          />
        );
      }

      if (total > 0) {
        return (
          <EmptyState
            icon={Layers}
            title="This page is empty"
            description={`There are ${String(total)} prototypes, but none on page ${String(resolved.page)}.`}
            action={
              <Button variant="outline" onClick={goToFirstPage}>
                Go to the first page
              </Button>
            }
          />
        );
      }

      return (
        <EmptyState
          icon={Layers}
          title="No prototypes yet"
          description={
            can.createPrototype
              ? 'A prototype is one thing under review — a flow, a screen, a landing page. Create one, then upload a ZIP of its HTML to start collecting comments.'
              : 'A prototype is one thing under review — a flow, a screen, a landing page. None have been created here yet.'
          }
          action={
            can.createPrototype ? (
              <Button onClick={openCreate}>
                <Plus aria-hidden="true" />
                New prototype
              </Button>
            ) : undefined
          }
        />
      );
    }

    return (
      <>
        <div
          className={cn(
            'grid grid-cols-1 gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-3',
            // Paging and filtering keep the old cards on screen; dim them so it
            // is obvious they are the previous answer, not the new one.
            query.isFetching && 'opacity-60',
          )}
        >
          {items.map((prototype) => (
            <PrototypeCard
              key={prototype.id}
              prototype={prototype}
              onEdit={setEditing}
              onDelete={setDeleting}
              onUpload={openUpload}
            />
          ))}
        </div>

        <PaginationBar
          page={resolved.page}
          pageSize={resolved.pageSize}
          total={total}
          onPageChange={handlePageChange}
          itemNoun="prototypes"
        />
      </>
    );
  };

  return (
    <div className="space-y-6">
      <PrototypesToolbar
        search={resolved.search}
        sort={resolved.sort}
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        onCreate={openCreate}
        canCreate={can.createPrototype}
        isFetching={query.isFetching}
      />

      {renderList()}

      <CreatePrototypeDialog
        projectId={projectId}
        open={isCreateOpen}
        onOpenChange={setCreateOpen}
      />

      <EditPrototypeDialog
        prototype={editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
          }
        }}
      />

      <DeletePrototypeDialog
        prototype={deleting}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
          }
        }}
      />

      <UploadVersionDialog
        prototype={uploading}
        onOpenChange={(open) => {
          if (!open) {
            setUploading(null);
          }
        }}
      />
    </div>
  );
};
